import * as _ from 'lodash';
import Promise = require('bluebird');
import { ComponentBase, ComponentOptionsBase } from '../../Common/ComponentBase';
import { Typeahead, TypeaheadApi, TypeaheadOptions } from '../../Widgets/Typeahead';
import { Utils } from '../../Utils/Utils';
import { FileUtils } from '../../Utils/FileUtils';
import { DateUtils } from '../../Utils/DateUtils';
import { StringUtils } from '../../Utils/StringUtils';
import { ArrayUtils } from '../../Utils/ArrayUtils';

export interface ProgressPanelComponentOptions extends ComponentOptionsBase {
    idPostfix: string;
    urlProgressStatus: string;
    urlStopProcess: string;
    urlHideProcessInfo: string;

    initialStatusInfo: IProgressPanelStatusInfo;

    localizer: {
        StatusQueued: string;
        StatusRunning: string;
        StatusCompleted: string;
        StatusCancelled: string;
        StatusNoData: string;
    }
}

interface IProgressPanelStatusInfo {
    succeeded: number;
    failed: number;
    remaining: number;
    progress: number;
    started: string;
    finished: string;
    status: string;
    errorText: string;
    taskId: string;
}

export class ProgressPanelComponent extends ComponentBase<ProgressPanelComponentOptions> {
    private _container: JQuery;
    private _progress: JQuery;
    private _status: JQuery;
    private _statusSucceeded: JQuery;
    private _statusFailed: JQuery;
    private _statusRemaining: JQuery;
    private _statusStarted: JQuery;
    private _statusFinished: JQuery;
    private _buttonStop: JQuery;
    private _buttonHide: JQuery;
    private _errorText: JQuery;
    private _taskLabel: JQuery;

    private _currentInfo: IProgressPanelStatusInfo

    private _urlGetStatus: string;
    private _urlStopProcess: string;
    private _urlHideProcessInfo: string;

    constructor(options: ProgressPanelComponentOptions) {
        super(options);

        this._currentInfo = null;

        this._urlGetStatus = URI(this._options.urlProgressStatus).toString();
        this._urlStopProcess = StringUtils.isNullOrEmpty(this._options.urlStopProcess) ? null : URI(this._options.urlStopProcess).toString();
        this._urlHideProcessInfo = StringUtils.isNullOrEmpty(this._options.urlHideProcessInfo) ? null : URI(this._options.urlHideProcessInfo).toString();
    }

    init(): void {
        var panelId = `progressPanel-${this._options.idPostfix}`;
        var containerId = `${panelId}-container`;
        var progressId = `${panelId}-progress`;
        var statusId = `${panelId}-status`;
        var statusSucceededId = `${panelId}-succeeded`;
        var statusFailedId = `${panelId}-failed`;
        var statusRemainingId = `${panelId}-remaining`;
        var statusStartedId = `${panelId}-started`;
        var statusFinishedId = `${panelId}-finished`;
        var buttonStopId = `${panelId}-btnStop`;
        var buttonHideId = `${panelId}-btnHide`;
        var errorTextId = `${panelId}-errorText`;
        var taskLabelId = `${panelId}-taskId`;

        this._container = Utils.getJQueryById(containerId);
        this._progress = Utils.getJQueryById(progressId);
        this._status = Utils.getJQueryById(statusId);
        this._statusSucceeded = Utils.getJQueryById(statusSucceededId);
        this._statusFailed = Utils.getJQueryById(statusFailedId);
        this._statusRemaining = Utils.getJQueryById(statusRemainingId);
        this._statusStarted = Utils.getJQueryById(statusStartedId);
        this._statusFinished = Utils.getJQueryById(statusFinishedId);
        this._buttonStop = Utils.getJQueryById(buttonStopId);
        this._buttonHide = Utils.getJQueryById(buttonHideId);
        this._errorText = Utils.getJQueryById(errorTextId);
        this._taskLabel = Utils.getJQueryById(taskLabelId);

        this.refreshProgressStatus = this.refreshProgressStatus.bind(this);
        setTimeout(this.refreshProgressStatus, 100);

        if (!StringUtils.isNullOrEmpty(this._urlStopProcess)) {
            this.stopProcess = this.stopProcess.bind(this);
            this._buttonStop.on('click', this.stopProcess);
        }

        if (!StringUtils.isNullOrEmpty(this._urlHideProcessInfo)) {
            this.hideProcessInfo = this.hideProcessInfo.bind(this);
            this._buttonHide.on('click', this.hideProcessInfo);
        }

        if (!Utils.isNullOrUndefined(this._options.initialStatusInfo)) {
            this.update(this._options.initialStatusInfo);
        }

        this._container.show();
    }

    private stopProcess(): boolean {
        var self = this;
        if (this.isRunning) {
            FileUtils.jsonPost<IProgressPanelStatusInfo>(this._urlStopProcess)
                .then(json => {
                    if (json) {
                        self.update(json);
                    }
                });
        }

        return false;
    }

    private hideProcessInfo(): boolean {
        location.href = this._urlHideProcessInfo;
        return false;
    }

    private refreshProgressStatus(): void {
        var self = this;
        var uri = URI(this._options.urlProgressStatus);

        FileUtils.readLocalJsonFile<IProgressPanelStatusInfo>(uri.toString(), false)
            .then(json => {
                if (json) {
                    self.update(json);
                }
            });
    }

    private get isRunning() {
        return !Utils.isNullOrUndefined(this._currentInfo) &&
            this._currentInfo.status !== 'Completed' &&
            this._currentInfo.status !== 'Cancelled' &&
            this._currentInfo.status !== 'NoData';
    }
        

    private update(info: IProgressPanelStatusInfo): void {
        this._currentInfo = info;
        this._status.text(this._options.localizer['Status' + info.status]);

        if (!Utils.isNullOrUndefined(info)) {
            if (info.status === 'Completed') {
                if (info.failed === 0) {
                    this._status.addClass('text-success');
                } else {
                    this._status.addClass('text-danger');
                }
            } else if (info.status === 'Cancelled') {
                this._status.addClass('text-danger');
            }
        }

        this._statusSucceeded.text(info.succeeded);
        this._statusFailed.text(info.failed);
        this._statusRemaining.text(info.remaining);
        this._statusStarted.text(info.started);
        this._statusFinished.text(info.finished);
        this._taskLabel.text(info.taskId);

        var progress = info.progress + '%';
        this._progress.css('width', progress);
        this._progress.text(progress);

        var hasErrorText = !StringUtils.isNullOrEmpty(info.errorText);
        Utils.toggleShow(this._errorText, hasErrorText);
        this._errorText.text(info.errorText || '');


        if (this.isRunning) {
            // start next task after 1sec
            setTimeout(this.refreshProgressStatus, 1000);
        }

        if (this._buttonStop.length === 1) {
            var hide = !Utils.isNullOrUndefined(info) &&
                info.status === 'Completed' ||
                info.status === 'Cancelled' ||
                info.status === 'NoData';

            Utils.toggleShow(this._buttonStop, !hide);
        }

        if (this._buttonHide.length === 1) {
            var hide = Utils.isNullOrUndefined(info) ||
                info.status === 'Queued' ||
                info.status === 'Running';

            Utils.toggleShow(this._buttonHide, !hide);
        }
    }
}


/** WEBPACK FOOTER **
 ** ./Typescript/Components/ProgressPanel/Component.ts
 **/